import { useCallback, useState } from "react";

import { AuthError, type ErrorInfo } from "@skilitics/designer-auth/error";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

export interface FormErrorInfo {
  form?: ErrorInfo;
  fields?: Record<string, true | ErrorInfo>;
}

export type CognitoErrorHandlingHook<F> = [handler: F, error: FormErrorInfo | null];

export const useCognitoErrorHandling = <Args extends unknown[], R>(
  innerHandler: (...args: Args) => R | Promise<R>,
): CognitoErrorHandlingHook<(...args: Args) => Promise<R | null>> => {
  const [error, setError] = useState<FormErrorInfo | null>(null);

  const handler = useCallback(
    async (...args: Args) => {
      setError(null);
      try {
        return await innerHandler(...args);
      } catch (rawError) {
        // seems we don't set any field-level errors at the moment.
        setError({ form: mapToErrorInfo(rawError) });
        return null;
      }
    },
    [innerHandler],
  );

  return [handler, error];
};

const mapToErrorInfo = (error: unknown): ErrorInfo => {
  if (error instanceof AuthError) {
    return { code: error.code, args: error.args };
  }

  console.error("Unhandled login error: %O", error);
  return { code: "unknownError" };
};

export const useErrorMessage = () => {
  const formatMessage = useFormatMessage(formatStrings);
  return useCallback(
    (error: ErrorInfo) => {
      return formatMessage(error.code, error.args);
    },
    [formatMessage],
  );
};

const formatStrings = {
  incorrectUserOrPassword: { en: "Incorrect email or password." },
  userNotConfirmed: { en: "User is not confirmed." },
  userNotFound: { en: "Email not verified." },
  userAlreadyExists: { en: "A user with this email already exists. Try logging in instead." },
  userDoesNotExist: { en: "User does not exist." },
  userDisabled: { en: "User is disabled." },
  attemptsExceeded: { en: "Too many attempts recently. Please wait before trying again." },
  // This flow doesn't properly work yet, it should send an email with the temporary password, but
  // we currently only send the verification code. When done correctly, the user shouldn't need to
  // request a code again, but go directly to the confirm screen.
  resetRequired: { en: "User password has been reset. Please use 'Forgot your password?' to recover." },
  // User should not see this if the client and server agree, so this should be a
  // temporary situation.
  signUpNotPermitted: { en: "Sorry, sign up is currently disabled" },
  signUpRejected: { en: "Sorry, this sign up is not permitted: {message}" },
  passwordTooShort: { en: "Password too short" },
  passwordRequiresUppercase: { en: "Password must contain an uppercase letter" },
  passwordRequiresLowercase: { en: "Password must contain a lowercase letter" },
  passwordRequiresNumbers: { en: "Password must contain a number" },
  passwordRequiresSymbols: { en: "Password must contain a symbol" },
  expiredCode: { en: "Expired code" },
  invalidCode: { en: "Invalid code" },

  unknownError: { en: "Something unexpected occurred. Try again, or let us know if the issue remains." },
} satisfies FormatMessages;
